"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { locales, localeFlags, type Locale } from "@/lib/i18n/config";

type Props = {
  current: Locale;
};

type Suggestion = {
  suggested: Locale;
  reason: "browser" | "country";
  country: string | null;
};

const SUPPORTED = new Set<Locale>(locales);

const PREF_COOKIE = "sahha-locale-pref";
const NEXT_INTL_COOKIE = "NEXT_LOCALE";

/**
 * Libellés du CTA principal écrits dans la LANGUE CIBLE.
 * Convention industrie (Google, Apple, GitHub) : on annonce le basculement
 * dans la langue où l'utilisateur va atterrir, pour qu'il valide qu'il la
 * comprend bien avant de cliquer.
 */
const SWITCH_CTA: Record<Locale, string> = {
  fr: "Continuer en français",
  ar: "المتابعة بالعربية",
  en: "Continue in English",
};

/**
 * Libellés du titre dans CHAQUE langue d'affichage. Le placeholder {lang}
 * est remplacé par le nom de la langue cible *traduit dans la langue
 * d'affichage* — jamais le label natif. Évite les phrases franglais comme
 * "Sahha is also available in Français".
 */

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

function writeCookieClient(name: string, value: string) {
  const oneYear = 60 * 60 * 24 * 365;
  const secure = typeof location !== "undefined" && location.protocol === "https:";
  // Domain partagé entre sahha.ma + pro.sahha.ma + admin.sahha.ma en prod.
  const domain =
    typeof location !== "undefined" && location.hostname.endsWith("sahha.ma")
      ? "; domain=.sahha.ma"
      : "";
  document.cookie = `${name}=${value}; path=/; max-age=${oneYear}; samesite=lax${
    secure ? "; secure" : ""
  }${domain}`;
}

/**
 * Bannière non-intrusive en bas de page qui suggère un changement de
 * langue lorsque la locale courante diffère de la langue navigateur.
 *
 * Conformité Google :
 *  - aucune redirection automatique (le visiteur choisit explicitement)
 *  - le contenu reste accessible dans la locale demandée par l'URL
 *  - pas d'interstitial bloquant (slide-in en bas, n'occulte pas le
 *    contenu principal — Core Web Vitals préservés)
 *  - hreflang reste source de vérité côté SEO (cf. layout)
 *
 * Détection déportée CÔTÉ CLIENT (fetch `/api/i18n/suggest`, `no-store`) —
 * PAS calculée en SSR dans le layout. Le layout `[locale]/layout.tsx` est
 * rendu avec `Cache-Control: public, s-maxage=3600` (cache CDN partagé,
 * indexé par path uniquement, cf. `reference_perf_cache_stack.md`) : tout
 * calcul basé sur les headers de la requête (Accept-Language, cf-ipcountry)
 * fait côté SSR se retrouve figé dans le HTML pour TOUS les visiteurs
 * suivants sur cette URL pendant jusqu'à 1h. Incident 2026-07-21 : popup
 * "Continue in English" affiché à des visiteurs Accept-Language: fr parce
 * que le premier hit ayant peuplé le cache pour `/` était anglophone. Même
 * pattern que `incident_cache_leak_session_2026_07_20.md` (Header.tsx →
 * AuthAwareUserMenu) : tout rendu personnalisé par visiteur doit être
 * déporté post-hydration, jamais calculé dans le SSR d'une page cachée.
 *
 * Persistance : cookies `sahha-locale-pref` (notre choix) + `NEXT_LOCALE`
 * (lu prioritairement par next-intl middleware avant Accept-Language).
 * Sans le second, next-intl re-redirige vers `/en` après le clic.
 */
export function LocaleSuggestionBanner({ current }: Props) {
  const pathname = usePathname() || "/";
  const t = useTranslations("common.localeSuggestion");

  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  // 1) Récupère la suggestion depuis la requête RÉELLE du client (jamais
  //    mise en cache CDN — cf. JSDoc ci-dessus). Skip si le visiteur a déjà
  //    un choix mémorisé.
  useEffect(() => {
    if (readCookie(PREF_COOKIE)) return;
    const controller = new AbortController();
    fetch(`/api/i18n/suggest?current=${encodeURIComponent(current)}`, {
      credentials: "same-origin",
      signal: controller.signal,
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { suggested?: string; reason?: string; country?: string | null } | null) => {
        if (!data || !data.suggested || !data.reason) return;
        // Comparaison insensible à la casse (fr / fr-FR / FR-fr → "fr") :
        // la locale suggérée doit être supportée ET RÉELLEMENT différente
        // de la locale courante — jamais de popup fr→fr.
        const suggestedLocale = data.suggested.trim().toLowerCase() as Locale;
        const currentLocale = current.trim().toLowerCase();
        if (!SUPPORTED.has(suggestedLocale) || suggestedLocale === currentLocale) return;
        if (data.reason !== "browser" && data.reason !== "country") return;
        setSuggestion({
          suggested: suggestedLocale,
          reason: data.reason,
          country: data.country ?? null,
        });
      })
      .catch(() => {
        // réseau indisponible / abort → pas de bannière, pas bloquant
      });
    return () => controller.abort();
  }, [current]);

  // 2) Une fois la suggestion connue, applique le même délai d'affichage
  //    qu'avant (1.5s) pour ne pas concurrencer le First Contentful Paint.
  useEffect(() => {
    if (!suggestion) return;
    if (readCookie(PREF_COOKIE)) return;
    const id = window.setTimeout(() => setVisible(true), 1500);
    return () => window.clearTimeout(id);
  }, [suggestion]);

  if (!visible || !suggestion) return null;
  const { suggested, reason, country } = suggestion;

  // Strip leading "/fr", "/ar", "/en" pour reconstruire l'URL dans la
  // locale suggérée. Ex: "/dentiste/casablanca" reste tel quel,
  // "/ar/dentiste/casablanca" → "/dentiste/casablanca".
  const cleanPath = pathname.replace(/^\/(fr|ar|en)(?=\/|$)/, "") || "/";
  const targetUrl =
    suggested === "fr" ? cleanPath : `/${suggested}${cleanPath === "/" ? "" : cleanPath}`;

  async function persistChoice(choice: Locale) {
    // 1) Cookie next-intl côté client (synchrone). next-intl middleware
    //    le lit AVANT Accept-Language quand `localeDetection: true`.
    writeCookieClient(NEXT_INTL_COOKIE, choice);
    // 2) Notre cookie de pref pour ne plus afficher la bannière.
    writeCookieClient(PREF_COOKIE, choice);
    // 3) Pose aussi côté server (avec domain=.sahha.ma cross-subdomain).
    //    AWAIT obligatoire avant le navigate sur mobile : iOS Safari et
    //    certains Chrome Android ne flushent pas toujours `document.cookie`
    //    avant `location.href = ...` → la première requête arrive sans
    //    cookie et le middleware re-redirige selon Accept-Language. Sans
    //    cet await, le bouton "ne fait rien" sur mobile (boucle).
    try {
      await fetch("/api/i18n/dismiss-suggestion", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ choice }),
        credentials: "same-origin",
      });
    } catch {
      // ignore : les cookies client sont déjà posés en fallback
    }
  }

  async function onSwitch() {
    setClosing(true);
    await persistChoice(suggested);
    // location.replace : pas d'entrée history vers l'ancienne locale
    // (le user ne peut pas revenir "back" sur la page non choisie).
    window.location.replace(targetUrl);
  }

  async function onStay() {
    setClosing(true);
    await persistChoice(current);
    window.setTimeout(() => setVisible(false), 200);
  }

  const subtitleKey = reason === "country" ? "subtitleCountry" : "subtitleBrowser";
  // Label de la langue suggérée TRADUIT dans la langue d'affichage
  // (en EN: "French" — pas "Français"). Évite les mix franglais.
  const suggestedLocalized = t(`languages.${suggested}` as never);
  const currentLocalized = t(`languages.${current}` as never);

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("title", { language: suggestedLocalized })}
      className={[
        "fixed z-[60] w-[min(92vw,360px)] transition-all duration-200 ease-out",
        // Mobile : centré bas avec safe-area iOS (au-dessus de la BottomNav).
        "left-1/2 -translate-x-1/2 bottom-[calc(5rem+env(safe-area-inset-bottom))]",
        // Desktop ≥ sm : ancré coin bas-droit (ou bas-gauche en RTL),
        //    pas de centrage. On override les classes mobile.
        current === "ar"
          ? "sm:left-4 sm:translate-x-0 sm:bottom-4"
          : "sm:left-auto sm:right-4 sm:translate-x-0 sm:bottom-4",
        closing ? "opacity-0 translate-y-2 sm:translate-x-0" : "opacity-100",
      ].join(" ")}
    >
      <div className="overflow-hidden rounded-2xl border border-[var(--sahha-border-default)] bg-white shadow-lg ring-1 ring-black/5">
        <div className="flex items-start gap-3 p-4">
          <span className="text-2xl leading-none" aria-hidden>
            {localeFlags[suggested]}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-[var(--sahha-text-900)]">
              {t("title", { language: suggestedLocalized })}
            </p>
            <p className="mt-0.5 text-xs text-[var(--sahha-text-500)]">
              {reason === "country" && country
                ? t(subtitleKey, { country })
                : t(subtitleKey, { language: suggestedLocalized })}
            </p>
          </div>
          <button
            type="button"
            onClick={onStay}
            aria-label={t("closeAria")}
            className="-mr-1 -mt-1 rounded-full p-1 text-[var(--sahha-text-400)] transition hover:bg-[var(--sahha-surface-2)] hover:text-[var(--sahha-text-700)]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="flex items-stretch gap-2 border-t border-[var(--sahha-border-subtle)] bg-[var(--sahha-surface-1)] px-4 py-3">
          <button
            type="button"
            onClick={onSwitch}
            // Bouton écrit dans la LANGUE CIBLE (convention Google/Apple) —
            // le user voit dans quelle langue il atterrit avant de cliquer.
            // dir="auto" pour que l'AR soit RTL même quand le current est LTR.
            dir="auto"
            className="flex-1 rounded-full bg-[var(--sahha-primary-600)] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[var(--sahha-primary-700)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-500)] focus:ring-offset-2"
          >
            {SWITCH_CTA[suggested]}
          </button>
          <button
            type="button"
            onClick={onStay}
            className="rounded-full px-4 py-2 text-xs font-semibold text-[var(--sahha-text-700)] transition hover:bg-[var(--sahha-surface-2)]"
          >
            {t("stayIn", { language: currentLocalized })}
          </button>
        </div>
      </div>
    </div>
  );
}
